import type { Flow } from './types';

export type MerchantContext = {
  id: number;
  company: string | null;
  currency: string | null;
  websiteUrl: string | null;
};

export class ApiError extends Error {
  status: number;

  statusText: string;

  path: string;

  responseBody: unknown;

  constructor({
    path,
    status,
    statusText,
    responseBody,
  }: {
    path: string;
    status: number;
    statusText: string;
    responseBody: unknown;
  }) {
    super(`Request failed for ${path}: ${status} ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.path = path;
    this.responseBody = responseBody;
  }
}

const parseErrorBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text || null;
};

export const apiFetch = async <T>(path: string, token: string): Promise<T> => {
  const response = await fetch(`${process.env.API_ROOT}${path}`, {
    headers: { Authorization: `Admin ${token}` },
  });

  if (!response.ok) {
    throw new ApiError({
      path,
      status: response.status,
      statusText: response.statusText,
      responseBody: await parseErrorBody(response),
    });
  }

  return response.json() as Promise<T>;
};

export const apiPost = async <T>(path: string, token: string, body: unknown): Promise<T> => {
  const response = await fetch(`${process.env.API_ROOT}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Admin ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new ApiError({
      path,
      status: response.status,
      statusText: response.statusText,
      responseBody: await parseErrorBody(response),
    });
  }

  return response.json() as Promise<T>;
};

export type SampleProduct = {
  name: string;
  imageSrc: string;
  imageAlt: string;
  price: number | null;
  handle: string;
};

export type GeneratedEmail = {
  flow: Flow;
  index: number;
  subject: string;
  preview: string;
  headline: string;
  body: string;
};

export type GenerateAllResponse = { emails: GeneratedEmail[] };
export type RegenerateResponse = GeneratedEmail;
export type AnalyzeBrandVoiceResponse = { analysis: string };
